import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyPortal } from '@/components/BodyPortal';
import { EmailView } from '@/components/ProfileMenu/EmailView';
import { LogoutConfirmDialog } from '@/components/LogoutConfirmDialog';
import { FamilyView } from '@/components/ProfileMenu/FamilyView';
import { MenuView } from '@/components/ProfileMenu/MenuView';
import { NotificationsView } from '@/components/ProfileMenu/NotificationsView';
import { PasswordView } from '@/components/ProfileMenu/PasswordView';
import { PhoneView } from '@/components/ProfileMenu/PhoneView';
import { UsernameView } from '@/components/ProfileMenu/UsernameView';
import { Modal } from '@/components/Modal';
import { Snackbar } from '@/components/Snackbar';
import { ProfileMenuView, SnackbarVariant } from '@/types/components';
import type { AddPhoneContext, FamilyMember } from '@/types/user';

interface Props {
  isOpen: boolean;
  initialView?: ProfileMenuView;
  onClose: () => void;
}

interface NavEntry {
  view: ProfileMenuView;
  member?: FamilyMember;
  addContext?: AddPhoneContext;
}

export const ProfileMenu: React.FC<Props> = ({
  isOpen,
  initialView = ProfileMenuView.Menu,
  onClose,
}) => {
  const { t } = useTranslation();
  const [stack, setStack] = useState<NavEntry[]>([{ view: initialView }]);
  const [wasOpen, setWasOpen] = useState(isOpen);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    message: '',
    variant: SnackbarVariant.Success,
  });

  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);

    if (isOpen) {
      setStack([{ view: initialView }]);
    }
  }

  const current = stack[stack.length - 1];
  const hasBack = stack.length > 1;

  const goBack = () => {
    const updatedStack = stack.length > 1 ? stack.slice(0, -1) : stack;
    setStack(updatedStack);
  };

  const openView = (view: ProfileMenuView) => setStack([...stack, { view }]);

  const openMemberView = (view: ProfileMenuView, member: FamilyMember) =>
    setStack([...stack, { view, member }]);

  const openAddView = (context: AddPhoneContext) =>
    setStack([...stack, { view: ProfileMenuView.Phone, addContext: context }]);

  const handleDetailSuccess = (message: string) => {
    goBack();
    setSnackbar({ message, variant: SnackbarVariant.Success });
    setIsSnackbarOpen(true);
  };

  const handleDetailError = () => {
    setSnackbar({
      message: t('global.errorOccurred'),
      variant: SnackbarVariant.Error,
    });
    setIsSnackbarOpen(true);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        isHiddenOnDesktop={isLogoutConfirmOpen}
        label={t('profileMenu.title')}
        className="md:h-[740px] md:max-h-[calc(100svh-80px)] md:w-[440px] md:rounded-2xl"
        onClose={onClose}
      >
        {current.view === ProfileMenuView.Menu && (
          <MenuView
            onClose={onClose}
            onOpenView={openView}
            onError={handleDetailError}
            onLogout={() => setIsLogoutConfirmOpen(true)}
          />
        )}
        {current.view === ProfileMenuView.Username && (
          <UsernameView
            onClose={onClose}
            onError={handleDetailError}
            onBack={goBack}
            onSuccess={() =>
              handleDetailSuccess(t('changeUsername.successToast'))
            }
          />
        )}
        {current.view === ProfileMenuView.Phone && (
          <PhoneView
            member={current.member}
            addContext={current.addContext}
            onClose={onClose}
            onError={handleDetailError}
            onBack={goBack}
            onSuccess={() => handleDetailSuccess(t('changePhone.successToast'))}
          />
        )}
        {current.view === ProfileMenuView.Email && (
          <EmailView
            onClose={onClose}
            onError={handleDetailError}
            onBack={goBack}
            onSuccess={() => handleDetailSuccess(t('changeEmail.successToast'))}
          />
        )}
        {current.view === ProfileMenuView.Password && (
          <PasswordView
            onClose={onClose}
            onError={handleDetailError}
            onBack={goBack}
            onSuccess={() =>
              handleDetailSuccess(t('changePassword.successToast'))
            }
          />
        )}
        {current.view === ProfileMenuView.Family && (
          <FamilyView
            onBack={goBack}
            onClose={onClose}
            onOpenPhone={(member) =>
              openMemberView(ProfileMenuView.Phone, member)
            }
            onOpenNotifications={(member) =>
              openMemberView(ProfileMenuView.Notifications, member)
            }
            onOpenAdd={openAddView}
          />
        )}
        {current.view === ProfileMenuView.Notifications && (
          <NotificationsView
            member={current.member}
            onBack={hasBack ? goBack : undefined}
            onClose={onClose}
            onError={handleDetailError}
            onChangePhone={() => openView(ProfileMenuView.Phone)}
            onChangeEmail={() => openView(ProfileMenuView.Email)}
          />
        )}
      </Modal>
      <LogoutConfirmDialog
        isOpen={isLogoutConfirmOpen}
        onClose={() => setIsLogoutConfirmOpen(false)}
      />
      <BodyPortal>
        <Snackbar
          message={snackbar.message}
          variant={snackbar.variant}
          isOpen={isSnackbarOpen}
          onDismiss={() => setIsSnackbarOpen(false)}
        />
      </BodyPortal>
    </>
  );
};
